import styled, { css } from 'styled-components';

import { COLORS } from '../../../utils/theme/colors';

const primary = css`
  ${({ theme }) => theme?.primary || '#015989'}
`;

export const OK_COLOR = COLORS.available;
export const OVER_COLOR = COLORS.amountStatusNotPaid;
export const EXTERNAL_COLOR = '#b9c2cc';
export const RESERVE_COLOR = '#e3e7eb';

export const Page = styled.div`
  background: #f3f5f7;
  border-radius: 16px;
  padding: 16px;

  @media (max-width: 576px) {
    padding: 8px;
  }

  /* O antd fica no azul padrão nesses componentes se o módulo não tingir (ver tenantColors) */
  .ant-spin-dot-item {
    background-color: ${primary};
  }

  .ant-switch-checked {
    background-color: ${primary};
  }

  .ant-btn-link {
    color: ${primary};
  }
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
`;

export const ToolbarText = styled.p`
  margin: 0;
  max-width: 720px;
  color: rgba(0, 0, 0, 0.6);
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 992px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const Card = styled.section`
  min-width: 0;
  overflow-x: auto;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
`;

export const CardTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: ${primary};
`;

export const CardText = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

export const Stepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  & + & {
    margin-top: 8px;
  }
`;

export const StepperLabel = styled.span`
  min-width: 56px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
`;

export const StepperValue = styled.span<{ $big?: boolean }>`
  min-width: 72px;
  text-align: center;
  font-weight: 700;
  font-size: ${({ $big }) => ($big ? '28px' : '16px')};
  color: ${primary};
`;

export const StationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(230px, 100%), 1fr));
  gap: 12px;
`;

export const StationCard = styled.div<{ $warning?: boolean }>`
  border: 1px solid ${({ $warning }) => ($warning ? '#faad14' : '#e8ecef')};
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const Hint = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
`;

export const ConnectorRow = styled.div<{ $charging: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border-radius: 10px;
  background: ${({ $charging }) => ($charging ? '#eef8f1' : '#f7f8fa')};
`;

export const CarButton = styled.button<{ $charging: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 8px;
  border: 1px dashed ${({ $charging }) => ($charging ? OK_COLOR : '#c8ced4')};
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-weight: 600;
  color: ${({ $charging }) => ($charging ? '#2f8a47' : 'rgba(0, 0, 0, 0.45)')};

  .anticon {
    font-size: 20px;
  }
`;

export const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ResultHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

export const Gauge = styled.div<{ $over: boolean }>`
  flex: 0 0 auto;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  border: 6px solid ${({ $over }) => ($over ? OVER_COLOR : OK_COLOR)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: border-color 0.3s;

  strong {
    font-size: 22px;
    line-height: 1.1;
    color: ${primary};
  }

  span {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
  }
`;

export const BarRow = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 96px;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  & + & {
    margin-top: 6px;
  }
`;

export const BarTrack = styled.div`
  position: relative;
  height: 14px;
  border-radius: 7px;
  background: #f0f2f4;
  overflow: hidden;
  display: flex;
`;

export const BarSegment = styled.div<{ $color: string; $width: number }>`
  height: 100%;
  width: ${({ $width }) => `${Math.max(0, $width)}%`};
  background: ${({ $color }) => $color};
  transition: width 0.3s;
`;

export const ConnectorsSegment = styled(BarSegment)`
  background: ${primary};
`;

export const LimitMark = styled.div<{ $left: number }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${({ $left }) => `${Math.min(100, $left)}%`};
  width: 2px;
  background: #333;
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

export const LegendDot = styled.span<{ $color?: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
  background: ${({ $color }) => $color || 'transparent'};
`;

export const LegendPrimaryDot = styled(LegendDot)`
  background: ${primary};
`;

export const Compare = styled.div<{ $gain: boolean }>`
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: 600;
  background: ${({ $gain }) => ($gain ? '#eef8f1' : '#fff')};
  border: 1px solid ${({ $gain }) => ($gain ? OK_COLOR : '#e8ecef')};
`;
